<template>
  <div class="free-account-modern">
    <!-- Loading Overlay Customizado -->
    <div v-if="loading" class="custom-loading-overlay">
      <div class="loading-container">
        <div class="loading-animation">
          <!-- Ícone principal animado -->
          <div class="main-icon">
            <i class="fas fa-rocket rocket-icon"></i>
          </div>

          <!-- Círculos de loading -->
          <div class="loading-circles">
            <div class="circle circle-1"></div>
            <div class="circle circle-2"></div>
            <div class="circle circle-3"></div>
          </div>
        </div>

        <!-- Mensagem principal -->
        <div class="loading-content">
          <h3 class="loading-title">{{ loadingMessage }}</h3>
          <p class="loading-subtitle">{{ loadingSubtitle }}</p>

          <!-- Barra de progresso animada -->
          <div class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
          </div>

          <!-- Etapas do processo -->
          <div class="loading-steps">
            <div
              class="step"
              :class="{ active: currentStep >= 1, completed: currentStep > 1 }"
            >
              <i class="fas fa-check-circle step-icon"></i>
              <span>Validando dados</span>
            </div>
            <div
              class="step"
              :class="{ active: currentStep >= 2, completed: currentStep > 2 }"
            >
              <i class="fas fa-server step-icon"></i>
              <span>Criando infraestrutura</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tela de Autenticação -->
    <div v-if="!isAuthenticated" class="auth-screen">
      <div class="auth-container">
        <div class="auth-card">
          <div class="auth-header">
            <div class="lock-icon">
              <i class="fas fa-shield-alt"></i>
            </div>
            <h2 class="auth-title">Acesso Restrito</h2>
            <p class="auth-subtitle">
              Digite suas credenciais para acessar o formulário de cadastro
            </p>
          </div>

          <div class="auth-content">
            <form @submit.prevent="authenticate" method="post">
              <div class="auth-form-group">
                <label class="auth-form-label">
                  <i class="fas fa-user form-icon"></i>
                  Login de Acesso
                </label>
                <input
                  type="text"
                  v-model="login_auth"
                  class="auth-form-input"
                  placeholder="Digite o login"
                  required
                  :disabled="authLoading"
                />
              </div>

              <div class="auth-form-group">
                <label class="auth-form-label">
                  <i class="fas fa-lock form-icon"></i>
                  Senha de Acesso
                </label>
                <div class="auth-password-container">
                  <input
                    :type="showAuthPassword ? 'text' : 'password'"
                    v-model="password_auth"
                    class="auth-form-input"
                    placeholder="Digite a senha"
                    required
                    :disabled="authLoading"
                  />
                  <button
                    type="button"
                    class="auth-password-toggle"
                    @click="showAuthPassword = !showAuthPassword"
                    :disabled="authLoading"
                  >
                    <i
                      :class="
                        showAuthPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                      "
                    ></i>
                  </button>
                </div>
              </div>

              <!-- Error Messages para Auth -->
              <div v-if="authErrors.length > 0" class="auth-errors">
                <div
                  v-for="(error, index) in authErrors"
                  :key="index"
                  class="auth-error-item"
                >
                  <i class="fas fa-exclamation-triangle mr-2"></i>
                  {{ error.message }}
                </div>
              </div>

              <div class="auth-form-actions">
                <button
                  type="submit"
                  :disabled="authLoading || !login_auth || !password_auth"
                  class="btn-authenticate"
                >
                  <i class="fas fa-unlock-alt mr-2"></i>
                  {{ authLoading ? "Verificando..." : "Acessar" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Formulário Principal (só aparece após autenticação) -->
    <div v-if="isAuthenticated">
      <!-- Header Section Melhorado -->
      <div class="account-header">
        <div class="header-content">
          <div class="header-badge">
            <i class="fas fa-gift mr-2"></i>
            OFERTA ESPECIAL
          </div>

          <h1 class="page-title">Crie sua Conta Gratuita</h1>

          <div class="subtitle-container">
            <p class="page-subtitle">
              Comece hoje mesmo e teste todas as funcionalidades por
            </p>
            <div class="trial-highlight">
              <span class="trial-days">7 DIAS</span>
              <span class="trial-text">TOTALMENTE GRÁTIS</span>
            </div>
          </div>

          <!-- Call to action visual -->
          <div class="header-cta">
            <i class="fas fa-arrow-down bounce-arrow"></i>
          </div>

          <!-- Botão de Logout -->
          <div class="logout-container">
            <button @click="logout" class="btn-logout" type="button">
              <i class="fas fa-sign-out-alt mr-2"></i>
              Sair
            </button>
          </div>
        </div>
      </div>

      <!-- Form Section -->
      <div class="form-container">
        <div class="form-card">
          <div class="card-header">
            <img
              id="logo"
              src="https://sistemclinupload.s3.amazonaws.com/logo.png"
              alt="SISTEMA CLIN"
            />
            <p class="card-subtitle">
              Preencha as informações abaixo para criar sua conta
            </p>
          </div>

          <div class="card-content">
            <form @submit.prevent="create" method="post">
              <div class="form-grid">
                <!-- CNPJ -->
                <div class="form-group full-width">
                  <label class="form-label required">
                    <i class="fas fa-building form-icon"></i>
                    CNPJ da Empresa
                  </label>
                  <input
                    type="text"
                    v-model="cnpj"
                    v-mask="'##.###.###/####-##'"
                    class="modern-form-input"
                    placeholder="00.000.000/0000-00"
                    :disabled="loading"
                    required
                  />
                </div>

                <!-- Nome -->
                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-user form-icon"></i>
                    Nome Completo do Responsável
                  </label>
                  <input
                    type="text"
                    v-model="name"
                    class="modern-form-input"
                    placeholder="Digite seu nome completo"
                    :disabled="loading"
                    required
                  />
                </div>

                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-phone form-icon"></i>
                    Telefone para contato
                  </label>
                  <input
                    type="text"
                    v-model="contact"
                    class="modern-form-input"
                    placeholder="Digite seu telefone"
                    :disabled="loading"
                    required
                  />
                </div>

                <!-- Email -->
                <div class="form-group full-width">
                  <label class="form-label required">
                    <i class="fas fa-envelope form-icon"></i>
                    E-mail
                  </label>
                  <input
                    type="email"
                    v-model="email"
                    class="modern-form-input"
                    placeholder="seu@email.com"
                    :disabled="loading"
                    required
                  />
                </div>

                <!-- Senha -->
                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-lock form-icon"></i>
                    Senha
                  </label>
                  <div class="password-container">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="password"
                      class="modern-form-input"
                      placeholder="Digite sua senha"
                      :disabled="loading"
                      required
                      @input="checkPasswordStrength"
                    />
                    <button
                      type="button"
                      class="password-toggle"
                      @click="showPassword = !showPassword"
                      :disabled="loading"
                    >
                      <i
                        :class="
                          showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                        "
                      ></i>
                    </button>
                  </div>
                  <!-- Indicador de força da senha -->
                  <div v-if="password" class="password-strength">
                    <div class="strength-bar">
                      <div
                        class="strength-fill"
                        :class="passwordStrengthClass"
                        :style="{ width: passwordStrengthWidth + '%' }"
                      ></div>
                    </div>
                    <span class="strength-text" :class="passwordStrengthClass">
                      {{ passwordStrengthText }}
                    </span>
                  </div>
                </div>

                <!-- Confirmar Senha -->
                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-lock form-icon"></i>
                    Confirmar Senha
                  </label>
                  <div class="password-container">
                    <input
                      :type="showConfirmPassword ? 'text' : 'password'"
                      v-model="confirm_password"
                      class="modern-form-input"
                      :class="{
                        error:
                          confirm_password && confirm_password !== password,
                      }"
                      placeholder="Confirme sua senha"
                      :disabled="loading"
                      required
                    />
                    <button
                      type="button"
                      class="password-toggle"
                      @click="showConfirmPassword = !showConfirmPassword"
                      :disabled="loading"
                    >
                      <i
                        :class="
                          showConfirmPassword
                            ? 'fas fa-eye-slash'
                            : 'fas fa-eye'
                        "
                      ></i>
                    </button>
                  </div>
                  <div
                    v-if="confirm_password && confirm_password !== password"
                    class="field-error"
                  >
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    As senhas não coincidem
                  </div>
                  <div
                    v-if="confirm_password && confirm_password === password"
                    class="field-success"
                  >
                    <i class="fas fa-check-circle mr-1"></i>
                    Senhas coincidem
                  </div>
                </div>

                <!-- Estado -->
                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-map-marker-alt form-icon"></i>
                    Estado
                  </label>
                  <select
                    v-model="uf_id"
                    class="modern-form-select"
                    :disabled="loading"
                    required
                    @change="city = ''"
                  >
                    <option value="">Selecione o estado</option>
                    <option v-for="uf in ufs" :key="uf.id" :value="uf.id">
                      {{ uf.name }} ({{ uf.initials }})
                    </option>
                  </select>
                </div>

                <!-- Cidade -->
                <div class="form-group">
                  <label class="form-label required">
                    <i class="fas fa-city form-icon"></i>
                    Cidade
                  </label>
                  <input
                    type="text"
                    v-model="city"
                    class="modern-form-input"
                    placeholder="Digite sua cidade"
                    :disabled="!uf_id || loading"
                    required
                  />
                </div>
              </div>

              <!-- Terms and Privacy -->
              <div class="terms-section">
                <label class="terms-checkbox">
                  <input
                    type="checkbox"
                    v-model="acceptTerms"
                    :disabled="loading"
                    required
                  />
                  <span class="checkmark"></span>
                  <span class="terms-text">
                    Aceito os
                    <a
                      target="_blank"
                      href="https://sistemaclin.com.br/termos-de-uso/"
                      class="terms-link"
                      >Termos de Uso</a
                    >
                    e
                    <a
                      target="_blank"
                      href="https://sistemaclin.com.br/politicas-de-privacidade/"
                      class="terms-link"
                      >Política de Privacidade</a
                    >
                  </span>
                </label>
              </div>

              <!-- Error Messages -->
              <ErrorsMessages v-if="errors.length > 0" :errors="errors" />

              <!-- Submit Button -->
              <div class="form-actions">
                <button
                  type="submit"
                  :disabled="loading || !isFormValid"
                  class="btn-create-account"
                >
                  <i class="fas fa-rocket mr-2"></i>
                  Criar Conta Gratuita
                </button>
              </div>

              <!-- Login Link -->
              <div class="login-section">
                <p class="login-text">
                  Já possui uma conta?
                  <a class="login-link" @click="$router.push('/')">
                    Fazer Login
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../services/api";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import toast from "../lib/ToastNotification";

export default {
  name: "FreeAccount",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      // Dados de autenticação
      isAuthenticated: false,
      login_auth: "",
      password_auth: "",
      showAuthPassword: false,
      authLoading: false,
      authErrors: [],

      // Dados do formulário principal
      ufs: [],
      errors: [],

      cnpj: "",
      name: "",
      email: "",
      password: "",
      confirm_password: "",
      uf_id: "",
      city: "",
      contact: "",

      acceptTerms: false,
      showPassword: false,
      showConfirmPassword: false,

      passwordStrength: 0,
      loading: false,

      // Loading states
      currentStep: 0,
      loadingMessage: "Aguarde, em instantes sua conta será criada",
      loadingSubtitle:
        "Estamos preparando tudo para você começar a usar nosso sistema",
    };
  },

  computed: {
    isFormValid() {
      return (
        this.cnpj &&
        this.name &&
        this.email &&
        this.password &&
        this.confirm_password &&
        this.password === this.confirm_password &&
        this.uf_id &&
        this.city &&
        this.contact &&
        this.acceptTerms
      );
    },

    passwordStrengthClass() {
      if (this.passwordStrength < 2) return "weak";
      if (this.passwordStrength < 4) return "medium";
      return "strong";
    },

    passwordStrengthText() {
      if (this.passwordStrength < 2) return "Fraca";
      if (this.passwordStrength < 4) return "Média";
      return "Forte";
    },

    passwordStrengthWidth() {
      return (this.passwordStrength / 4) * 100;
    },
  },

  created() {
    this.getUfs();
  },

  methods: {
    async authenticate() {
      try {
        this.authLoading = true;
        this.authErrors = [];

        let { login_auth, password_auth } = this;

        let params = {
          login_auth,
          password_auth,
        };

        const response = await api.post("/free-account-auth", params);

        const { success } = response.data;

        if (success) {
          this.isAuthenticated = true;
          toast("Acesso autorizado!", "success");
        } else {
          this.authErrors = [
            { message: "Login ou senha inválidos. Tente novamente." },
          ];
        }

        this.authLoading = false;
      } catch (error) {
        this.authLoading = false;
        this.authErrors = [
          { message: "Erro interno do servidor. Tente novamente." },
        ];
        console.log(error);
      }
    },

    logout() {
      this.isAuthenticated = false;
      this.login_auth = "";
      this.password_auth = "";
      this.authErrors = [];

      // Limpa os dados do formulário
      this.cnpj = "";
      this.name = "";
      this.email = "";
      this.password = "";
      this.confirm_password = "";
      this.uf_id = "";
      this.city = "";
      this.contact = "";
      this.acceptTerms = false;
      this.errors = [];

      toast("Sessão encerrada", "info");
    },

    async getUfs() {
      try {
        const response = await api.get("/ufs");
        const { ufs } = response.data;
        this.ufs = ufs;
      } catch (error) {
        console.log(error);
      }
    },

    checkPasswordStrength() {
      let strength = 0;
      const password = this.password;

      // Critérios de força
      if (password.length >= 8) strength++;
      if (/[a-z]/.test(password)) strength++;
      if (/[A-Z]/.test(password)) strength++;
      if (/[0-9]/.test(password)) strength++;
      if (/[^A-Za-z0-9]/.test(password)) strength++;

      this.passwordStrength = strength;
    },

    simulateLoadingSteps() {
      // Simula as etapas do processo de criação
      this.currentStep = 1;
      this.loadingMessage = "Validando suas informações";
      this.loadingSubtitle = "Verificando CNPJ e dados cadastrais";

      setTimeout(() => {
        this.currentStep = 2;
        this.loadingMessage = "Criando sua infraestrutura";
        this.loadingSubtitle = "Configurando sua conta";
      }, 1500);
    },

    async create() {
      try {
        this.loading = true;
        this.errors = [];
        this.currentStep = 0;

        // Inicia a simulação das etapas
        this.simulateLoadingSteps();

        let {
          login_auth,
          password_auth,
          cnpj,
          name,
          email,
          password,
          confirm_password,
          uf_id,
          city,
          contact,
        } = this;

        let params = {
          login_auth,
          password_auth,
          cnpj,
          name,
          email,
          password,
          confirm_password,
          uf_id,
          city,
          contact,
        };

        const response = await api.post("/free-account", params);

        const { error, errors } = response.data;

        if (!error) {
          // Completa todas as etapas
          this.currentStep = 3;
          this.loadingMessage = "Conta criada com sucesso!";
          this.loadingSubtitle = "Redirecionando para login...";

          toast("Conta criada com sucesso!", "success");

          setTimeout(() => {
            this.$router.push("/");
          }, 2000);
        } else {
          this.errors = errors;
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        this.errors = [
          { message: "Erro interno do servidor. Tente novamente." },
        ];
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
#logo {
  width: 200px;
  object-fit: contain;
  margin-bottom: 5px;
}

/* Auth Screen Styles */
.auth-screen {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.auth-container {
  max-width: 450px;
  width: 100%;
}

.auth-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.auth-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 32px 28px;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
}

.lock-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
}

.auth-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.auth-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
}

.auth-content {
  padding: 28px;
}

.auth-form-group {
  margin-bottom: 20px;
}

.auth-form-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.auth-form-input {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: white;
  width: 100%;
}

.auth-form-input:focus {
  border-color: #1e293b;
  box-shadow: 0 0 0 4px rgba(30, 41, 59, 0.1);
  outline: none;
}

.auth-form-input:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}

.auth-password-container {
  position: relative;
}

.auth-password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.auth-password-toggle:hover:not(:disabled) {
  color: #374151;
  background: #f3f4f6;
}

.auth-password-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.auth-errors {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.auth-error-item {
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.auth-error-item:last-child {
  margin-bottom: 0;
}

.auth-form-actions {
  margin-top: 24px;
}

.btn-authenticate {
  width: 100%;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(30, 41, 59, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-authenticate:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(30, 41, 59, 0.4);
}

.btn-authenticate:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Logout Button */
.logout-container {
  position: absolute;
  top: 24px;
  right: 24px;
}

.btn-logout {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-logout:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

/* Custom Loading Overlay */
.custom-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0f65b0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.loading-container {
  max-width: 500px;
  text-align: center;
  color: white;
  padding: 40px;
}

.loading-animation {
  position: relative;
  margin-bottom: 40px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-icon {
  position: relative;
  z-index: 2;
}

.rocket-icon {
  font-size: 48px;
  color: #fbbf24;
  animation: rocketFloat 2s ease-in-out infinite;
}

@keyframes rocketFloat {
  0%,
  100% {
    transform: translateY(0px) rotate(-5deg);
  }
  50% {
    transform: translateY(-10px) rotate(5deg);
  }
}

.loading-circles {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.circle {
  position: absolute;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 2s linear infinite;
}

.circle-1 {
  width: 80px;
  height: 80px;
  top: -40px;
  left: -40px;
}

.circle-2 {
  width: 100px;
  height: 100px;
  top: -50px;
  left: -50px;
  animation-duration: 3s;
  animation-direction: reverse;
}

.circle-3 {
  width: 120px;
  height: 120px;
  top: -60px;
  left: -60px;
  animation-duration: 4s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-content {
  margin-bottom: 30px;
}

.loading-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
  font-family: "WorkSans-SemiBold", sans-serif;
  animation: titlePulse 2s ease-in-out infinite;
}

@keyframes titlePulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.loading-subtitle {
  font-size: 16px;
  margin: 0 0 30px 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.progress-container {
  margin-bottom: 30px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
  border-radius: 3px;
  animation: progressFill 4s ease-out infinite;
}

@keyframes progressFill {
  0% {
    width: 0%;
  }
  25% {
    width: 30%;
  }
  50% {
    width: 60%;
  }
  75% {
    width: 85%;
  }
  100% {
    width: 100%;
  }
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
  opacity: 0.5;
}

.step.active {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(8px);
}

.step.completed {
  background: rgba(16, 185, 129, 0.3);
}

.step-icon {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.3s ease;
}

.step.active .step-icon {
  color: #fbbf24;
  animation: stepPulse 1s ease-in-out infinite;
}

.step.completed .step-icon {
  color: #10b981;
}

@keyframes stepPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.step span {
  font-size: 14px;
  font-weight: 500;
}

.free-account-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  padding: 0;
  overflow-x: hidden;
}

/* Header Section Melhorado */
.account-header {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  padding: 48px 24px 40px;
  text-align: center;
  color: white;
  min-height: 30vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }
}

.page-title {
  font-size: 42px;
  font-weight: 800;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
  color: white;
  line-height: 1.1;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.page-subtitle {
  font-size: 18px;
  margin: 0 0 16px 0;
  opacity: 0.9;
  line-height: 1.5;
  color: white;
  font-weight: 400;
}

.trial-highlight {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
}

.trial-days {
  font-size: 48px;
  font-weight: 900;
  color: #fbbf24;
  text-shadow: 0 4px 20px rgba(251, 191, 36, 0.4);
  line-height: 0.9;
  letter-spacing: 2px;
}

.trial-text {
  font-size: 16px;
  font-weight: 700;
  color: #fbbf24;
  text-transform: uppercase;
  letter-spacing: 3px;
}

.header-cta {
  margin-bottom: 24px;
}

.bounce-arrow {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.7);
  animation: bounce-arrow 2s ease-in-out infinite;
}

@keyframes bounce-arrow {
  0%,
  100% {
    transform: translateY(0px);
    opacity: 0.7;
  }
  50% {
    transform: translateY(8px);
    opacity: 1;
  }
}

/* Form Container */
.form-container {
  max-width: 900px;
  margin: -50px auto 0;
  padding: 0 24px 40px;
  position: relative;
}

.form-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 28px;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
}

.card-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 15px;
  font-weight: 400;
}

.card-content {
  padding: 32px;
}

/* Form Styling */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.form-label.required::after {
  content: "*";
  color: #ef4444;
  margin-left: 4px;
  font-weight: 700;
}

.form-icon {
  color: #6b7280;
  margin-right: 8px;
  font-size: 14px;
}

.modern-form-input,
.modern-form-select {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: white;
  width: 100%;
}

.modern-form-input:focus,
.modern-form-select:focus {
  border-color: #0f65b0;
  box-shadow: 0 0 0 4px rgba(15, 101, 176, 0.1);
  outline: none;
}

.modern-form-input:disabled,
.modern-form-select:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}

.modern-form-input.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

/* Password Container */
.password-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.password-toggle:hover:not(:disabled) {
  color: #374151;
  background: #f3f4f6;
}

.password-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Password Strength */
.password-strength {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.strength-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 3px;
}

.strength-fill.weak {
  background: #ef4444;
}

.strength-fill.medium {
  background: #f59e0b;
}

.strength-fill.strong {
  background: #10b981;
}

.strength-text {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.strength-text.weak {
  color: #ef4444;
}

.strength-text.medium {
  color: #f59e0b;
}

.strength-text.strong {
  color: #10b981;
}

/* Field Messages */
.field-error,
.field-success {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
}

.field-error {
  color: #ef4444;
}

.field-success {
  color: #10b981;
}

/* Terms Section */
.terms-section {
  margin-bottom: 28px;
}

.terms-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  position: relative;
}

.terms-checkbox input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  background: white;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: 2px;
}

.terms-checkbox input[type="checkbox"]:checked + .checkmark {
  background: #0f65b0;
  border-color: #0f65b0;
}

.terms-checkbox input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: 700;
}

.terms-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

.terms-link {
  color: #0f65b0;
  text-decoration: none;
  font-weight: 600;
}

.terms-link:hover {
  text-decoration: underline;
}

/* Form Actions */
.form-actions {
  margin-top: 24px;
  margin-bottom: 24px;
}

.btn-create-account {
  width: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 16px;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-create-account:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(16, 185, 129, 0.4);
}

.btn-create-account:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Login Section */
.login-section {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.login-text {
  color: #6b7280;
  margin: 0;
  font-size: 14px;
}

.login-link {
  color: #0f65b0;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
}

.login-link:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .auth-screen {
    padding: 16px;
  }

  .auth-header {
    padding: 24px 20px;
  }

  .auth-content {
    padding: 20px;
  }

  .lock-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }

  .auth-title {
    font-size: 20px;
  }

  .logout-container {
    position: static;
    margin-bottom: 16px;
    text-align: center;
  }

  .loading-container {
    padding: 20px;
    max-width: 90%;
  }

  .loading-title {
    font-size: 20px;
  }

  .loading-subtitle {
    font-size: 14px;
  }

  .loading-steps {
    gap: 12px;
  }

  .step {
    padding: 10px 12px;
  }

  .rocket-icon {
    font-size: 40px;
  }

  .circle-1 {
    width: 60px;
    height: 60px;
    top: -30px;
    left: -30px;
  }

  .circle-2 {
    width: 80px;
    height: 80px;
    top: -40px;
    left: -40px;
  }

  .circle-3 {
    width: 100px;
    height: 100px;
    top: -50px;
    left: -50px;
  }

  .account-header {
    padding: 32px 16px 24px;
    min-height: 45vh;
  }

  .page-title {
    font-size: 28px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 16px;
  }

  .trial-days {
    font-size: 36px;
  }

  .trial-text {
    font-size: 14px;
    letter-spacing: 2px;
  }

  .form-container {
    padding: 0 16px 32px;
    margin-top: -32px;
  }

  .card-header,
  .card-content {
    padding: 24px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}

@media (max-width: 480px) {
  .loading-container {
    padding: 16px;
  }

  .loading-steps {
    text-align: center;
  }

  .step {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .account-header {
    min-height: 40vh;
    padding: 24px 16px 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .trial-days {
    font-size: 32px;
  }

  .trial-text {
    font-size: 12px;
  }

  .page-subtitle {
    font-size: 15px;
  }

  .modern-form-input,
  .modern-form-select {
    padding: 12px 14px;
  }

  .form-container {
    margin-top: -24px;
  }
}
</style>